import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Grid, TextField, MenuItem, Button, Chip, Switch, FormControlLabel, Alert, Typography,
  IconButton, Tooltip, InputAdornment, CircularProgress, Paper
} from "@mui/material";
import { Add, Delete, Refresh, Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import { aiCallApi, useAICallOverview, VOICES, VOICE_LABELS, MODELS, TICKET_MODELS, DAY_NAMES, dhaka } from "./useAICalling";
import { AICallingPage, SectionCard, Loading, ErrorState } from "./AICallingComponents";

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const str = (v, fallback = "") => (v === undefined || v === null ? fallback : String(v));

// same defaults as the backend (models/AICallSettings.js)
const SILENCE_CHECK_TEXT = "হ্যালো, আপনি কি আমার কথা শুনতে পাচ্ছেন?";
const SILENCE_GOODBYE_TEXT = "মনে হচ্ছে আপনার কথা শুনতে পাচ্ছি না। পরে আবার কথা হবে। ধন্যবাদ, আল্লাহ হাফেজ।";

const toForm = (s = {}) => ({
  model: s.model || "claude-haiku-5-5",
  agent: {
    name: str(s.agent?.name),
    voice: s.agent?.voice || "Priya",
    greeting: str(s.agent?.greeting),
    prompt: str(s.agent?.prompt),
  },
  limits: {
    callWindows: (s.limits?.callWindows?.length ? s.limits.callWindows : [{ start: "12:00", end: "13:00" }]).map((w) => ({
      start: str(w.start),
      end: str(w.end),
    })),
    preferredDays: Array.isArray(s.limits?.preferredDays) ? s.limits.preferredDays.slice() : [],
    perUserDays: str(s.limits?.perUserDays, "14"),
    maxCallSeconds: str(s.limits?.maxCallSeconds, "180"),
    concurrency: str(s.limits?.concurrency, "5"),
    dailyBudgetBdt: str(s.limits?.dailyBudgetBdt, "300"),
    retryAttempts: str(s.limits?.retryAttempts, "1"),
    retryAfterMinutes: str(s.limits?.retryAfterMinutes, "120"),
  },
  eligibility: {
    requireIntent: !!s.eligibility?.requireIntent,
    womenOnlyIfStartedCheckout: !!s.eligibility?.womenOnlyIfStartedCheckout,
  },
  recordings: { enabled: !!s.recordings?.enabled, retentionDays: str(s.recordings?.retentionDays, "30") },
  inbound: {
    enabled: !!s.inbound?.enabled,
    voice: str(s.inbound?.voice, "Priya"),
    greeting: str(s.inbound?.greeting),
    prompt: str(s.inbound?.prompt),
    maxCallSeconds: str(s.inbound?.maxCallSeconds, "600"),
  },
  ticketAi: {
    enabled: s.ticketAi?.enabled !== false,
    model: str(s.ticketAi?.model, "claude-sonnet-5-5"),
    agentMode: str(s.ticketAi?.agent?.mode, "auto"),
    agentModel: str(s.ticketAi?.agent?.model, "opus"),
    agentEffort: str(s.ticketAi?.agent?.effort, "medium"),
    agentBudget: str(s.ticketAi?.agent?.maxBudgetUsd, "3"),
    agentTimeout: str(s.ticketAi?.agent?.timeoutMinutes, "15"),
  },
  silence: {
    waitSeconds: str(s.silence?.waitSeconds, "8"),
    checks: str(s.silence?.checks, "2"),
    checkText: str(s.silence?.checkText, SILENCE_CHECK_TEXT),
    goodbyeText: str(s.silence?.goodbyeText, SILENCE_GOODBYE_TEXT),
  },
});

/** The sections the API takes, with numbers as numbers (used for the payload and to spot changes). */
const toSections = (f) => ({
  model: f.model,
  agent: { name: f.agent.name.trim(), voice: f.agent.voice, greeting: f.agent.greeting.trim(), prompt: f.agent.prompt.trim() },
  limits: {
    callWindows: f.limits.callWindows.map((w) => ({ start: w.start, end: w.end })),
    preferredDays: [...new Set(f.limits.preferredDays)].sort((a, b) => a - b),
    perUserDays: Number(f.limits.perUserDays),
    maxCallSeconds: Number(f.limits.maxCallSeconds),
    concurrency: Number(f.limits.concurrency),
    dailyBudgetBdt: Number(f.limits.dailyBudgetBdt),
    retryAttempts: Number(f.limits.retryAttempts),
    retryAfterMinutes: Number(f.limits.retryAfterMinutes),
  },
  eligibility: { ...f.eligibility },
  recordings: { enabled: f.recordings.enabled, retentionDays: Number(f.recordings.retentionDays) },
  inbound: {
    enabled: f.inbound.enabled,
    voice: f.inbound.voice,
    greeting: f.inbound.greeting.trim(),
    prompt: f.inbound.prompt.trim(),
    maxCallSeconds: Number(f.inbound.maxCallSeconds),
  },
  ticketAi: {
    enabled: f.ticketAi.enabled,
    model: f.ticketAi.model,
    agent: {
      mode: f.ticketAi.agentMode,
      model: f.ticketAi.agentModel,
      effort: f.ticketAi.agentEffort,
      maxBudgetUsd: Number(f.ticketAi.agentBudget),
      timeoutMinutes: Number(f.ticketAi.agentTimeout),
    },
  },
  silence: {
    waitSeconds: Number(f.silence.waitSeconds),
    checks: Number(f.silence.checks),
    checkText: f.silence.checkText.trim(),
    goodbyeText: f.silence.goodbyeText.trim(),
  },
});

const whole = (v, min, max) => v !== "" && Number.isInteger(Number(v)) && Number(v) >= min && Number(v) <= max;

const validate = (f, maxChannels) => {
  const a = f.agent;
  if (!a.name.trim() || a.name.trim().length > 60) return "Agent name: 1–60 characters";
  if (!VOICES.includes(a.voice)) return "Pick a voice";
  if (!a.greeting.trim() || a.greeting.trim().length > 600) return "Greeting: 1–600 characters";
  if (!a.prompt.trim() || a.prompt.trim().length > 8000) return "Prompt: 1–8000 characters";
  if (!MODELS.some((m) => m.value === f.model)) return "Pick a model";
  const l = f.limits;
  if (!l.callWindows.length || l.callWindows.length > 4) return "Call windows: 1 to 4 time ranges";
  for (const w of l.callWindows) {
    if (!HHMM.test(w.start) || !HHMM.test(w.end) || w.start === w.end) return "Each call window needs a start and an end time";
  }
  if (!whole(l.perUserDays, 1, 365)) return "Days between calls to a member: 1–365";
  if (!whole(l.maxCallSeconds, 30, 1800)) return "Longest call: 30–1800 seconds";
  if (!whole(l.concurrency, 1, Math.min(100, maxChannels || 100))) return `Calls at once: 1–${Math.min(100, maxChannels || 100)} (the SIP line's channels)`;
  const budget = Number(l.dailyBudgetBdt);
  if (l.dailyBudgetBdt === "" || !Number.isFinite(budget) || budget < 0 || budget > 100000) return "Daily budget: ৳0–100,000";
  if (!whole(l.retryAttempts, 0, 5)) return "Retries: 0–5";
  if (!whole(l.retryAfterMinutes, 5, 1440)) return "Retry after: 5–1440 minutes";
  if (!whole(f.recordings.retentionDays, 1, 365)) return "Keep recordings: 1–365 days";
  if (!VOICES.includes(f.inbound.voice)) return "Pick a voice for the support agent";
  if (f.inbound.greeting.trim().length > 600) return "Support greeting: at most 600 characters";
  if (f.inbound.prompt.trim().length > 20000) return "Support prompt: at most 20,000 characters";
  if (!whole(f.inbound.maxCallSeconds, 60, 1800)) return "Longest support call: 60–1800 seconds";
  const agentBudget = Number(f.ticketAi.agentBudget);
  if (f.ticketAi.agentBudget === "" || !Number.isFinite(agentBudget) || agentBudget < 0.5 || agentBudget > 20) return "Ticket agent: most per ticket $0.5–20";
  if (!whole(f.ticketAi.agentTimeout, 3, 60)) return "Ticket agent: time limit 3–60 minutes";
  const q = f.silence;
  if (!whole(q.waitSeconds, 4, 30)) return "Wait before asking: 4–30 seconds";
  if (!whole(q.checks, 0, 3)) return "Times to ask: 0–3";
  if (!q.checkText.trim() || q.checkText.trim().length > 300) return "\"Can you hear me\" line: 1–300 characters";
  if (!q.goodbyeText.trim() || q.goodbyeText.trim().length > 300) return "Goodbye line: 1–300 characters";
  return null;
};

const AICallingSettings = () => {
  const { overview, loading, error, fetch } = useAICallOverview();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const lastKey = useRef("");

  useEffect(() => {
    fetch();
  }, [fetch]);

  const saved = overview?.settings;
  const savedKey = saved ? JSON.stringify(toSections(toForm(saved))) : "";
  // refill only when the saved settings change, so a refresh doesn't wipe unsaved edits
  useEffect(() => {
    if (!saved || savedKey === lastKey.current) return;
    lastKey.current = savedKey;
    setForm(toForm(saved));
  }, [saved, savedKey]);

  const maxChannels = Number(saved?.sip?.maxChannels) || 5;

  const changed = useMemo(() => {
    if (!form || !saved) return {};
    const now = toSections(form);
    const before = toSections(toForm(saved));
    return Object.fromEntries(Object.keys(now).filter((k) => JSON.stringify(now[k]) !== JSON.stringify(before[k])).map((k) => [k, now[k]]));
  }, [form, saved]);
  const dirty = Object.keys(changed).length > 0;

  const setIn = (section, field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [section]: { ...f[section], [field]: value } }));
  };
  const setWindow = (i, key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      limits: { ...f.limits, callWindows: f.limits.callWindows.map((w, j) => (j === i ? { ...w, [key]: value } : w)) },
    }));
  };
  const addWindow = () =>
    setForm((f) => ({ ...f, limits: { ...f.limits, callWindows: [...f.limits.callWindows, { start: "19:00", end: "21:00" }] } }));
  const removeWindow = (i) =>
    setForm((f) => ({ ...f, limits: { ...f.limits, callWindows: f.limits.callWindows.filter((_, j) => j !== i) } }));
  const toggleDay = (d) =>
    setForm((f) => {
      const days = f.limits.preferredDays.includes(d) ? f.limits.preferredDays.filter((x) => x !== d) : [...f.limits.preferredDays, d];
      return { ...f, limits: { ...f.limits, preferredDays: days } };
    });

  const save = async () => {
    const err = validate(form, maxChannels);
    if (err) return toast.error(err);
    if (!dirty) return toast.info("Nothing to change");
    setSaving(true);
    const res = await aiCallApi.saveSettings(changed);
    setSaving(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || "Settings saved");
    if (res.body?.engineReloaded === false) toast.info("The calling process will use the new settings when it runs.");
    lastKey.current = ""; // take whatever the server saved
    await fetch({ quiet: true });
  };

  const actions = <Button variant="outlined" startIcon={<Refresh />} onClick={() => fetch()} disabled={loading}>Refresh</Button>;
  const page = (children) => (
    <AICallingPage title="AI Calling Settings" subtitle="The agent, when it may call, and who it may call" actions={actions}>
      {children}
    </AICallingPage>
  );

  if (loading && !overview) return page(<Loading />);
  if (!overview) return page(<ErrorState error={error || "Could not load the settings"} onRetry={() => fetch()} />);
  if (!form) return page(<Loading />);

  const greetingLen = form.agent.greeting.length;
  const promptLen = form.agent.prompt.length;

  return page(
    <>
      <SectionCard title="Agent" subtitle="How the AI introduces itself and what it is told to do (Bangla)">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Agent name" fullWidth size="small" value={form.agent.name} onChange={setIn("agent", "name")}
              inputProps={{ maxLength: 60, lang: "bn" }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Voice" fullWidth size="small" value={form.agent.voice} onChange={setIn("agent", "voice")}
              helperText="Soniox voice; every voice speaks Bangla">
              {VOICES.map((v) => <MenuItem key={v} value={v}>{VOICE_LABELS[v] || v}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Greeting" fullWidth multiline minRows={3} value={form.agent.greeting} onChange={setIn("agent", "greeting")}
              inputProps={{ maxLength: 600, lang: "bn" }} error={!form.agent.greeting.trim()}
              helperText={`The first thing the agent says when the member answers. {name} = the member's name, {reason} = why we call (unfinished payment, wallet top-up, package expiring, or a general reason) — filled in per member · ${greetingLen}/600`} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Prompt" fullWidth multiline minRows={8} maxRows={24} value={form.agent.prompt} onChange={setIn("agent", "prompt")}
              inputProps={{ maxLength: 8000, lang: "bn" }} error={!form.agent.prompt.trim()}
              helperText={`Instructions for the agent. Safety rules (says it's an AI, never asks for a PIN or OTP, no payment on the phone) are always added · ${promptLen}/8000`} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Model">
        <TextField select label="AI model" size="small" value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
          sx={{ minWidth: 260, maxWidth: "100%" }} helperText="Writes the agent's replies (Soniox hears the member and speaks). Used for new calls">
          {MODELS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
      </SectionCard>

      <SectionCard title="Limits">
        <Typography variant="subtitle2">Call windows (Bangladesh time)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Campaign and ladder calls are only placed inside these times. A callback the member asked for may be outside them, but never in quiet hours.
        </Typography>
        {form.limits.callWindows.map((w, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
            <TextField label="From" type="time" size="small" value={w.start} onChange={setWindow(i, "start")}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} sx={{ width: 140 }} error={!HHMM.test(w.start)} />
            <TextField label="To" type="time" size="small" value={w.end} onChange={setWindow(i, "end")}
              InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} sx={{ width: 140 }} error={!HHMM.test(w.end) || w.start === w.end} />
            <Tooltip title={form.limits.callWindows.length <= 1 ? "At least one window" : "Remove"}>
              <span>
                <IconButton onClick={() => removeWindow(i)} disabled={form.limits.callWindows.length <= 1} aria-label="Remove window">
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addWindow} disabled={form.limits.callWindows.length >= 4}>Add window</Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>1–4 windows</Typography>

        <Typography variant="subtitle2" sx={{ mt: 3 }}>Preferred days</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {DAY_NAMES.map((name, d) => {
            const on = form.limits.preferredDays.includes(d);
            return (
              <Chip key={name} label={name} clickable color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"}
                onClick={() => toggleDay(d)} />
            );
          })}
        </Box>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Days between calls to a member" type="number" fullWidth size="small" value={form.limits.perUserDays}
              onChange={setIn("limits", "perUserDays")} inputProps={{ min: 1, max: 365, step: 1 }}
              helperText="At most one AI call per member in this many days (1–365)" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Longest call (seconds)" type="number" fullWidth size="small" value={form.limits.maxCallSeconds}
              onChange={setIn("limits", "maxCallSeconds")} inputProps={{ min: 30, max: 1800, step: 1 }}
              helperText="The agent wraps up and hangs up at this limit (30–1800; 1200 = 20 min)" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Calls at once" type="number" fullWidth size="small" value={form.limits.concurrency}
              onChange={setIn("limits", "concurrency")} inputProps={{ min: 1, max: Math.min(100, maxChannels), step: 1 }}
              helperText={`At most ${maxChannels} (the SIP line's channels)`} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Daily budget" type="number" fullWidth size="small" value={form.limits.dailyBudgetBdt}
              onChange={setIn("limits", "dailyBudgetBdt")} inputProps={{ min: 0, max: 100000, step: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start">৳</InputAdornment> }}
              helperText="No new calls once today's AI-call cost reaches this. 0 = no calls." />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Retries" type="number" fullWidth size="small" value={form.limits.retryAttempts}
              onChange={setIn("limits", "retryAttempts")} inputProps={{ min: 0, max: 5, step: 1 }}
              helperText="Tries again when not answered or busy (0–5)" />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Retry after (minutes)" type="number" fullWidth size="small" value={form.limits.retryAfterMinutes}
              onChange={setIn("limits", "retryAfterMinutes")} inputProps={{ min: 5, max: 1440, step: 1 }}
              helperText="5–1440, still inside the call windows" />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Support tickets" subtitle="AI answers tickets from the app and website, 24/7">
        <FormControlLabel
          control={<Switch checked={form.ticketAi.enabled} onChange={setIn("ticketAi", "enabled")} />}
          label="AI answers new support tickets"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Uses the support prompt below and the member's own account. Answers within a few minutes (up to 3 times per ticket);
          anything a person must handle is set to high priority with an "AI: needs the team" note.
        </Typography>
        <TextField select label="Model" size="small" value={form.ticketAi.model} onChange={setIn("ticketAi", "model")} sx={{ minWidth: 260 }}
          helperText="Sonnet: accurate (about ৳1–4 per ticket). Haiku: cheaper.">
          {TICKET_MODELS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>

        <Typography variant="subtitle1" sx={{ mt: 3 }}>Ticket agent</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          A ticket the quick answer can't settle (a bug, an unclear payment, a failing upload) is investigated by Claude Code on
          the server: it reads the member's account, logs and our code, but changes nothing. It replies in short; a bug in our code
          is written down with why and how to fix it (AI Calling → Ticket agent).
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select label="Mode" fullWidth size="small" value={form.ticketAi.agentMode} onChange={setIn("ticketAi", "agentMode")}
              helperText="Draft only: every reply waits for you on the Ticket agent page">
              <MenuItem value="auto">Auto-send</MenuItem>
              <MenuItem value="draft">Draft only</MenuItem>
              <MenuItem value="off">Off (quick answer only)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select label="Model" fullWidth size="small" value={form.ticketAi.agentModel} onChange={setIn("ticketAi", "agentModel")}>
              <MenuItem value="opus">opus</MenuItem>
              <MenuItem value="sonnet">sonnet</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField select label="Effort" fullWidth size="small" value={form.ticketAi.agentEffort} onChange={setIn("ticketAi", "agentEffort")}>
              {["low", "medium", "high", "xhigh", "max"].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField label="Most per ticket ($)" type="number" fullWidth size="small" value={form.ticketAi.agentBudget}
              onChange={setIn("ticketAi", "agentBudget")} inputProps={{ min: 0.5, max: 20, step: 0.5 }} />
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <TextField label="Time limit (min)" type="number" fullWidth size="small" value={form.ticketAi.agentTimeout}
              onChange={setIn("ticketAi", "agentTimeout")} inputProps={{ min: 3, max: 60, step: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="When the member is quiet" subtitle="Outbound and inbound calls">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          After the agent finishes speaking, if the member says nothing for the wait time, the agent asks the line below. It asks
          up to the set number of times; if there is still no answer it says the goodbye line and hangs up. Anything the member
          says starts the count again.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Wait before asking (seconds)" type="number" fullWidth size="small" value={form.silence.waitSeconds}
              onChange={setIn("silence", "waitSeconds")} inputProps={{ min: 4, max: 30, step: 1 }}
              helperText="Silence after the agent stops speaking (4–30)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Times to ask" type="number" fullWidth size="small" value={form.silence.checks}
              onChange={setIn("silence", "checks")} inputProps={{ min: 0, max: 3, step: 1 }}
              helperText="0 = say goodbye after the first silence (0–3)" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="&quot;Can you hear me?&quot; line" fullWidth size="small" value={form.silence.checkText} onChange={setIn("silence", "checkText")}
              inputProps={{ maxLength: 300, lang: "bn" }}
              helperText={`Said word for word · ${form.silence.checkText.length}/300`} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Goodbye line" fullWidth size="small" value={form.silence.goodbyeText} onChange={setIn("silence", "goodbyeText")}
              inputProps={{ maxLength: 300, lang: "bn" }}
              helperText={`Said word for word, then the call ends · ${form.silence.goodbyeText.length}/300`} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Who may be called">
        <FormControlLabel
          control={<Switch checked={form.eligibility.requireIntent} onChange={setIn("eligibility", "requireIntent")} />}
          label="Only members who started a checkout or have a package"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}>
          When on, members who never started a bKash payment and have no active package are skipped.
        </Typography>
        <FormControlLabel
          control={<Switch checked={form.eligibility.womenOnlyIfStartedCheckout} onChange={setIn("eligibility", "womenOnlyIfStartedCheckout")} />}
          label="Women only if they started a checkout themselves"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: { xs: 0, sm: 6 } }}>
          When on, a woman member is called only if she started a payment herself; having a package isn't enough.
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          Always skipped: the do-not-call list, unverified numbers, deactivated accounts, members who turned promotional messages
          off, and members who paid since the campaign started.
        </Alert>
      </SectionCard>

      <SectionCard title="Recordings">
        <FormControlLabel
          control={<Switch checked={form.recordings.enabled} onChange={setIn("recordings", "enabled")} />}
          label="Record calls"
        />
        <Box sx={{ mt: 1 }}>
          <TextField label="Keep recordings (days)" type="number" size="small" value={form.recordings.retentionDays}
            onChange={setIn("recordings", "retentionDays")} inputProps={{ min: 1, max: 365, step: 1 }}
            helperText="Deleted automatically after this many days (1–365)" sx={{ maxWidth: 320 }} fullWidth />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Recordings stay on the server (no public link). Only admins with recordings access can play them, and every play and download is logged.
        </Typography>
      </SectionCard>

      <SectionCard title="Inbound calls">
        <FormControlLabel
          control={<Switch checked={form.inbound.enabled} onChange={setIn("inbound", "enabled")} />}
          label="Answer calls to the AI extension with the support agent"
        />
        <Typography variant="body2" color="text.secondary">
          The support agent can create a support ticket and end the call. Off: calls to the extension aren't answered by the AI.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField select label="Support voice" fullWidth size="small" value={form.inbound.voice} onChange={setIn("inbound", "voice")}>
              {VOICES.map((v) => <MenuItem key={v} value={v}>{VOICE_LABELS[v] || v}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Longest support call (seconds)" type="number" fullWidth size="small" value={form.inbound.maxCallSeconds}
              onChange={setIn("inbound", "maxCallSeconds")} inputProps={{ min: 60, max: 1800, step: 30 }}
              helperText="Support calls run longer than marketing calls (60–1800)" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Support greeting" fullWidth multiline minRows={2} value={form.inbound.greeting} onChange={setIn("inbound", "greeting")}
              inputProps={{ maxLength: 600, lang: "bn" }}
              helperText={`What the support agent says when it picks up (empty = built-in) · ${form.inbound.greeting.length}/600`} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Support prompt" fullWidth multiline minRows={6} maxRows={24} value={form.inbound.prompt} onChange={setIn("inbound", "prompt")}
              inputProps={{ maxLength: 20000, lang: "bn" }}
              helperText={`Instructions for the support agent (empty = built-in). Safety rules are always added · ${form.inbound.prompt.length}/20000`} />
          </Grid>
        </Grid>
      </SectionCard>

      <Paper
        elevation={3}
        sx={{ position: "sticky", bottom: 0, zIndex: 2, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography variant="caption" color="text.secondary">
          {saved?.updatedAt ? `Last changed ${dhaka(saved.updatedAt)}${saved.updatedByName ? ` by ${saved.updatedByName}` : ""}` : "Not changed yet"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {dirty && <Chip size="small" color="warning" variant="outlined" label="Unsaved changes" />}
          <Button disabled={!dirty || saving} onClick={() => setForm(toForm(saved))}>Undo changes</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={save} disabled={saving || !dirty}>
            Save
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default AICallingSettings;
